import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { PenLine, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { API } from "@/lib/api";
import { toast } from "sonner";

const MOODS = [
  { id: "lugn", label: "Lugn" },
  { id: "tankfull", label: "Tankfull" },
  { id: "ledsen", label: "Ledsen" },
  { id: "orolig", label: "Orolig" },
  { id: "tacksam", label: "Tacksam" },
  { id: "hoppfull", label: "Hoppfull" },
];

const DiaryPage = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [writing, setWriting] = useState(false);
  const [content, setContent] = useState("");
  const [mood, setMood] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchEntries = () => {
    API.get("/diary").then(res => {
      setEntries(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => { fetchEntries(); }, []);

  const saveEntry = async () => {
    if (!content.trim()) return;
    setSaving(true);
    try {
      await API.post("/diary", { content: content.trim(), mood: mood || null });
      setContent("");
      setMood("");
      setWriting(false);
      fetchEntries();
      toast.success("Sparat");
    } catch { toast.error("Kunde inte spara"); }
    setSaving(false);
  };

  const deleteEntry = async (id) => {
    try {
      await API.delete(`/diary/${id}`);
      fetchEntries();
      toast.success("Raderat");
    } catch { toast.error("Kunde inte radera"); }
  };

  const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString("sv-SE", { day: "numeric", month: "long", year: "numeric" });
  };

  const formatTime = (iso) => {
    const d = new Date(iso);
    return d.toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit" });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-10 h-10 border-4 border-[#A0655A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      <div className="pt-24 sm:pt-28 pb-8 sm:pb-20 px-4 sm:px-6">
        <div className="max-w-2xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10">
            <p className="text-sm uppercase tracking-[0.25em] text-[#A0655A] font-medium mb-4">Fri skrivyta</p>
            <h1 className="heading-font text-3xl sm:text-4xl font-medium text-[#111111] mb-3" data-testid="diary-page-title">Min dagbok</h1>
            <p className="text-[#111111]/50 text-base max-w-md mx-auto leading-relaxed">
              Skriv fritt om vad du tänker, känner eller behöver uttrycka.
            </p>
          </motion.div>

          {/* Write button or form */}
          {!writing ? (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mb-10 text-center">
              <Button onClick={() => setWriting(true)} className="bg-[#A0655A] hover:bg-[#8B5549] text-white rounded-full px-8 py-6 text-base" data-testid="new-diary-btn">
                <PenLine size={18} className="mr-2" /> Skriv en ny anteckning
              </Button>
            </motion.div>
          ) : (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-10">
              <div className="bg-white border border-[#EBE3DF] rounded-2xl p-6">
                <Textarea
                  value={content}
                  onChange={e => setContent(e.target.value)}
                  placeholder="Vad bär du på just nu?"
                  className="rounded-xl border-[#EBE3DF] focus:border-[#A0655A] resize-none text-base min-h-[150px] mb-4"
                  data-testid="diary-textarea"
                />
                
                <p className="text-xs text-[#111111]/40 mb-2">Hur känner du dig?</p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {MOODS.map(m => (
                    <button
                      key={m.id}
                      onClick={() => setMood(mood === m.id ? "" : m.id)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                        mood === m.id ? 'bg-[#A0655A] text-white' : 'bg-[#F9F3EF] text-[#111111]/50 hover:bg-[#EBE3DF]'
                      }`}
                      data-testid={`mood-${m.id}`}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>

                <div className="flex gap-3 justify-end">
                  <Button onClick={() => { setWriting(false); setContent(""); setMood(""); }} variant="outline" className="rounded-full">Avbryt</Button>
                  <Button onClick={saveEntry} disabled={!content.trim() || saving} className="bg-[#A0655A] hover:bg-[#8B5549] text-white rounded-full" data-testid="save-diary-btn">
                    {saving ? "Sparar..." : "Spara"}
                  </Button>
                </div>
              </div>
            </motion.div>
          )}

          {/* Entries */}
          {entries.length > 0 ? (
            <div className="space-y-4">
              {entries.map((entry, idx) => (
                <motion.div
                  key={entry.id}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 * idx }}
                  className="bg-white border border-[#EBE3DF] rounded-2xl p-6 group"
                  data-testid={`diary-entry-${entry.id}`}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <p className="text-xs text-[#111111]/40">{formatDate(entry.created_at)}, {formatTime(entry.created_at)}</p>
                      {entry.mood && <span className="text-xs bg-[#F9F3EF] text-[#A0655A] px-2 py-0.5 rounded-full mt-1 inline-block">{MOODS.find(m => m.id === entry.mood)?.label || entry.mood}</span>}
                    </div>
                    <button
                      onClick={() => deleteEntry(entry.id)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity text-[#111111]/20 hover:text-red-400"
                      data-testid={`delete-diary-${entry.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <p className="text-[#111111]/70 leading-relaxed text-sm whitespace-pre-wrap">{entry.content}</p>
                </motion.div>
              ))}
            </div>
          ) : !writing && (
            <div className="text-center py-12">
              <p className="text-[#111111]/30 text-sm">Du har inte skrivit något ännu.</p>
              <p className="text-[#111111]/30 text-sm">Tryck på knappen ovan för att börja.</p>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default DiaryPage;
